import { useMemo, type FC } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { cx } from "../../utils/classnames";
import { Select } from "../forms/Select";
import { Tooltip, TooltipContent, TooltipTrigger } from "../Tooltip";
import { containerClass, counterStyles, TabButtonStyles } from "./utils";
import type {
  Tab,
  TabProps,
  TabSideElement,
  TabStyle,
  TabSize,
  Orientation,
  IconElement,
  CounterElement
} from "./types";

interface TabCounterProps {
  count: number;
  tabStyle: TabStyle;
  active: boolean;
  disabled?: boolean;
}

const TabCounter: FC<TabCounterProps> = ({
  count,
  tabStyle,
  active,
  disabled
}) => {
  const getStateKey = () => {
    if (disabled) return "disabled";
    if (active) return "active";
    return "inactive";
  };
  const stateKey = getStateKey();
  return (
    <span
      className={cx(
        "inline-flex items-center text-center text-xs font-medium",
        counterStyles[stateKey][tabStyle]
      )}
    >
      {count}
    </span>
  );
};

// --- sub-components ---

interface TabSideElementProps {
  element?: TabSideElement;
  tabStyle: TabStyle;
  active: boolean;
  disabled?: boolean;
}

const TabSideElementView: FC<TabSideElementProps> = ({
  element,
  tabStyle,
  active,
  disabled
}) => {
  if (!element) return null;
  if (element.type === "icon") {
    return (
      <HugeiconsIcon
        icon={element.component}
        size="20"
        className="shrink-0"
        stroke="currentColor"
        strokeWidth={2}
      />
    );
  }
  return (
    <TabCounter
      count={element.count}
      tabStyle={tabStyle}
      active={active}
      disabled={disabled}
    />
  );
};

// --- component ---

const TabsInner = <T,>({
  orientation = "horizontal",
  fullWidthTabs = true,
  collapseOnMobile = false,
  tabStyle = "ghost",
  size,
  tabs,
  active,
  onChange,
  className
}: TabProps<T>) => {
  const isLine = tabStyle === "line";
  const isHorizontal = orientation === "horizontal";

  const wrapperClass = useMemo(
    () =>
      cx(
        isLine && isHorizontal
          ? "flex gap-3 items-start"
          : containerClass[tabStyle][orientation],
        collapseOnMobile && "hidden md:flex",
        className
      ),
    [tabStyle, orientation, className, isLine, isHorizontal, collapseOnMobile]
  );

  const activeTab = tabs.find((t) => t.value === active);

  return (
    <>
      {collapseOnMobile ? (
        <Select
          id="tabs-mobile-select"
          className="md:hidden w-full"
          value={activeTab ? String(activeTab.value) : ""}
          onChange={(e) => {
            const tab = tabs.find((t) => String(t.value) === e.target.value);
            if (tab && !tab.disabled) onChange(tab.value);
          }}
          options={tabs.map((tab) => ({
            value: String(tab.value),
            label:
              typeof tab.label === "string" || typeof tab.label === "object"
                ? (tab.label as string | JSX.Element)
                : String(tab.value),
            isDisabled: tab.disabled
          }))}
        />
      ) : null}
      <div
        className={wrapperClass}
        role="tablist"
        aria-orientation={orientation}
      >
        {tabs.map((tab) => {
          const button = (
            <TabButton
              key={String(tab.value)}
              tab={tab}
              active={tab.value === active}
              tabStyle={tabStyle}
              size={size ?? "sm"}
              orientation={orientation}
              fullWidth={fullWidthTabs}
              onClick={() => {
                if (!tab.disabled) onChange(tab.value);
              }}
            />
          );
          if (!tab.disabledTooltip) return button;
          return (
            <Tooltip key={String(tab.value)}>
              <TooltipTrigger asChild>
                <span className="inline-flex">{button}</span>
              </TooltipTrigger>
              <TooltipContent className="w-full max-w-[370px] rounded-lg z-[100] border bg-alpha-800 p-4 text-sm text-content-1000-inverted">
                {tab.disabledTooltip}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </>
  );
};

// --- Tab button ---

interface TabButtonProps<T> {
  tab: Tab<T>;
  active: boolean;
  tabStyle: TabStyle;
  size: TabSize;
  orientation: Orientation;
  fullWidth: boolean;
  onClick: () => void;
}

const TabButtonInner = <T,>({
  tab,
  active,
  tabStyle,
  size,
  orientation,
  fullWidth,
  onClick
}: TabButtonProps<T>) => {
  const styles = new TabButtonStyles({
    tabStyle,
    size,
    orientation,
    active,
    disabled: tab.disabled,
    fullWidth
  });

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      disabled={tab.disabled}
      onClick={onClick}
      className={cx(
        "group font-bold transition-colors whitespace-nowrap outline-none",
        styles.focusClass,
        styles.layoutClass,
        styles.sizeClass,
        styles.stateClass,
        styles.borderClass,
        styles.paddingClass,
        styles.widthClass
      )}
    >
      <TabSideElementView
        element={tab.leftElement}
        tabStyle={tabStyle}
        active={active}
        disabled={tab.disabled}
      />
      <span>{tab.label}</span>
      <TabSideElementView
        element={tab.rightElement}
        tabStyle={tabStyle}
        active={active}
        disabled={tab.disabled}
      />
    </button>
  );
};

const TabButton = TabButtonInner;

export const Tabs = TabsInner;
export type { Tab, TabProps, TabSideElement, IconElement, CounterElement };
